import type {
  ExtendedBusinessEntity,
  SupabaseClient,
  Tables,
  TablesUpdate,
} from "@repo/supabase/database-types"
import { logger } from "@repo/utils/logger"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import {
  createInPredefinedListFilter,
  DataTableQueryBuilder,
} from "~/lib/utils/datatable-query-builder"
import { QUERYABLE_BUSINESS_ENTITY_STATUSES } from "~/modules/business-entities/business-entities.constants"
import type {
  AddBusinessEntityCurrencyData,
  CreateBusinessEntityData,
  CreateInvoicePeriodData,
  DeleteInvoicePeriodData,
  RemoveBusinessEntityCurrencyData,
  UpdateBusinessEntityData,
  UpdateInvoicePeriodData,
} from "./business-entities.schema"

export type BusinessEntityInvoicePeriod =
  Tables<"business_entity_invoice_periods">

export const createBusinessEntitiesService = (supabase: SupabaseClient) => {
  return new BusinessEntitiesService(supabase)
}

class BusinessEntitiesService {
  log = logger.child({
    module: "business-entities-service",
  })

  constructor(private readonly supabase: SupabaseClient) {}

  async getFromSlug({ slug }: { slug: string }) {
    const { data, error } = await this.supabase
      .from("extended_business_entities")
      .select("*")
      .eq("slug", slug)
      .maybeSingle()

    if (error) {
      this.log.error("Error fetching business entity", { error })
      throw new Error(error.message)
    }

    if (!data) {
      this.log.warn("Business entity not found", { slug })
      throw new Error("Business entity not found")
    }

    return data
  }

  async getManyExtended(filters?: DataTableBaseSearch): Promise<{
    businessEntities: ExtendedBusinessEntity[]
    count: number
  }> {
    const builder = new DataTableQueryBuilder(this.supabase, {
      table: "extended_business_entities",
      searchField: "fts_business_entity",
      defaultSort: { field: "created_at", ascending: false },
      filters: {
        status: createInPredefinedListFilter(
          "status",
          QUERYABLE_BUSINESS_ENTITY_STATUSES,
        ),
      },
    })

    const { data, count } = await builder.execute<ExtendedBusinessEntity>({
      page: 1,
      page_size: 20,
      ...filters,
    })

    return {
      businessEntities: data,
      count,
    }
  }

  async getInvoicePeriods({ businessEntityId }: { businessEntityId: string }) {
    const { data, error } = await this.supabase
      .from("business_entity_invoice_periods")
      .select("*")
      .eq("business_entity_id", businessEntityId)
      .order("start_date", { ascending: false })

    if (error) {
      this.log.error("Error fetching business entity invoice periods", {
        error,
      })
      throw new Error(error.message)
    }

    return data
  }

  async getCurrencies({ businessEntityId }: { businessEntityId: string }) {
    const { data, error } = await this.supabase
      .from("business_entity_currencies")
      .select("*")
      .eq("business_entity_id", businessEntityId)

    if (error) {
      this.log.error("Error fetching business entity currencies", { error })
      throw new Error(error.message)
    }

    return data
  }

  async archive({
    businessEntityId,
  }: {
    businessEntityId: string
  }): Promise<void> {
    const { error } = await this.supabase.rpc("soft_delete_business_entities", {
      p_id: businessEntityId,
    })

    if (error) {
      this.log.error("Error archiving business entity", { error })
      throw new Error(error.message)
    }

    this.log.info("Business entity archived successfully", { businessEntityId })
  }

  async create(data: CreateBusinessEntityData): Promise<{ id: string }> {
    this.log.info("Creating business entity", { name: data.name })

    // Create the business entity first (unit_id will be auto-generated by trigger)
    const { data: businessEntity, error: businessEntityError } =
      await this.supabase
        .from("business_entities")
        .insert({
          name: data.name,
          status: data.status,
          default_currency: data.defaultCurrency,
          default_locale: data.defaultLocale,
          unit_id: crypto.randomUUID(), // Temporary, will be overridden by trigger
        })
        .select("id, unit_id")
        .single()

    if (businessEntityError || !businessEntity) {
      this.log.error("Error inserting business entity", {
        error: businessEntityError,
      })
      throw new Error("Failed to create business entity")
    }

    // Create address if provided, using the business entity's unit_id
    let billingAddressId: string | undefined
    if (data.address) {
      const { data: address, error: addressError } = await this.supabase
        .from("addresses")
        .insert({
          address_line_1: data.address.addressLine1,
          address_line_2: data.address.addressLine2,
          city: data.address.city,
          state: data.address.state,
          postal_code: data.address.postalCode,
          country: data.address.country,
          unit_id: businessEntity.unit_id,
        })
        .select("id")
        .single()

      if (addressError) {
        this.log.error("Error creating address", { error: addressError })
        throw new Error("Failed to create address")
      }

      billingAddressId = address.id
    }

    // Create banking details if provided
    let bankingDetailsId: string | undefined
    if (data.bankingDetails) {
      const { data: bankingDetails, error: bankingError } = await this.supabase
        .from("banking_details")
        .insert({
          bank_name: data.bankingDetails.bankName,
          bank_address: data.bankingDetails.bankAddress,
          swift_code: data.bankingDetails.swiftCode,
          account_name: data.bankingDetails.accountName,
          account_number: data.bankingDetails.accountNumber,
          unit_id: businessEntity.unit_id,
        })
        .select("id")
        .single()

      if (bankingError) {
        this.log.error("Error creating banking details", {
          error: bankingError,
        })
        throw new Error("Failed to create banking details")
      }

      bankingDetailsId = bankingDetails.id
    }

    // Update the business entity with address and banking details if any were created
    if (billingAddressId || bankingDetailsId) {
      const { error: updateError } = await this.supabase
        .from("business_entities")
        .update({
          billing_address_id: billingAddressId,
          banking_details_id: bankingDetailsId,
        })
        .eq("id", businessEntity.id)

      if (updateError) {
        this.log.error("Error updating business entity with related IDs", {
          error: updateError,
        })
        throw new Error("Failed to update business entity")
      }
    }

    this.log.info("Business entity created successfully", {
      businessEntityId: businessEntity.id,
    })
    return { id: businessEntity.id }
  }

  async update(data: UpdateBusinessEntityData): Promise<{ id: string }> {
    this.log.info("Updating business entity", { id: data.id, name: data.name })

    // First get the existing business entity
    const { data: existingEntity, error: getError } = await this.supabase
      .from("business_entities")
      .select("billing_address_id, banking_details_id, unit_id")
      .eq("id", data.id)
      .single()

    if (getError) {
      this.log.error("Error getting existing business entity", {
        error: getError,
      })
      throw new Error("Business entity not found")
    }

    // Handle address update/creation
    let billingAddressId: string | undefined =
      existingEntity.billing_address_id || undefined

    if (data.address) {
      if (existingEntity.billing_address_id) {
        // Update existing address
        const { error: addressError } = await this.supabase
          .from("addresses")
          .update({
            address_line_1: data.address.addressLine1 ?? null,
            address_line_2: data.address.addressLine2 ?? null,
            city: data.address.city ?? null,
            state: data.address.state ?? null,
            postal_code: data.address.postalCode ?? null,
            country: data.address.country ?? null,
          })
          .eq("id", existingEntity.billing_address_id)

        if (addressError) {
          this.log.error("Error updating address", { error: addressError })
          throw new Error("Failed to update address")
        }
      } else {
        // Create new address
        const { data: address, error: addressError } = await this.supabase
          .from("addresses")
          .insert({
            address_line_1: data.address.addressLine1 ?? null,
            address_line_2: data.address.addressLine2 ?? null,
            city: data.address.city ?? null,
            state: data.address.state ?? null,
            postal_code: data.address.postalCode ?? null,
            country: data.address.country ?? null,
            unit_id: existingEntity.unit_id,
          })
          .select("id")
          .single()

        if (addressError) {
          this.log.error("Error creating address", { error: addressError })
          throw new Error("Failed to create address")
        }

        billingAddressId = address.id
      }
    }

    // Handle banking details update/creation
    let bankingDetailsId: string | undefined =
      existingEntity.banking_details_id || undefined

    if (data.bankingDetails) {
      if (existingEntity.banking_details_id) {
        // Update existing banking details
        const { error: bankingError } = await this.supabase
          .from("banking_details")
          .update({
            bank_name: data.bankingDetails.bankName ?? null,
            bank_address: data.bankingDetails.bankAddress ?? null,
            swift_code: data.bankingDetails.swiftCode ?? null,
            account_name: data.bankingDetails.accountName ?? null,
            account_number: data.bankingDetails.accountNumber ?? null,
          })
          .eq("id", existingEntity.banking_details_id)

        if (bankingError) {
          this.log.error("Error updating banking details", {
            error: bankingError,
          })
          throw new Error("Failed to update banking details")
        }
      } else {
        // Create new banking details
        const { data: bankingDetails, error: bankingError } =
          await this.supabase
            .from("banking_details")
            .insert({
              bank_name: data.bankingDetails.bankName ?? null,
              bank_address: data.bankingDetails.bankAddress ?? null,
              swift_code: data.bankingDetails.swiftCode ?? null,
              account_name: data.bankingDetails.accountName ?? null,
              account_number: data.bankingDetails.accountNumber ?? null,
              unit_id: existingEntity.unit_id,
            })
            .select("id")
            .single()

        if (bankingError) {
          this.log.error("Error creating banking details", {
            error: bankingError,
          })
          throw new Error("Failed to create banking details")
        }

        bankingDetailsId = bankingDetails.id
      }
    }

    // Update the business entity
    const updateData: TablesUpdate<"business_entities"> = {
      name: data.name,
      status: data.status,
      billing_address_id: billingAddressId,
      banking_details_id: bankingDetailsId,
    }

    if (data.defaultCurrency !== undefined) {
      updateData.default_currency =
        data.defaultCurrency === "" ? null : data.defaultCurrency
    }
    if (data.defaultLocale !== undefined) {
      updateData.default_locale =
        data.defaultLocale === "" ? null : data.defaultLocale
    }

    const { data: businessEntity, error: businessEntityError } =
      await this.supabase
        .from("business_entities")
        .update(updateData)
        .eq("id", data.id)
        .select("id")
        .single()

    if (businessEntityError || !businessEntity) {
      this.log.error("Error updating business entity", {
        error: businessEntityError,
      })
      throw new Error("Failed to update business entity")
    }

    this.log.info("Business entity updated successfully", {
      businessEntityId: businessEntity.id,
    })
    return { id: businessEntity.id }
  }

  async getOverviewStats(businessEntitySlug: string): Promise<{
    memberCount: number
    clientCount: number
    projectCount: number
    resourceCount: number
  }> {
    this.log.info("Getting business entity overview stats", {
      businessEntitySlug,
    })

    // 1. Get business entity by slug
    const businessEntity = await this.getFromSlug({ slug: businessEntitySlug })

    // 2. Get member count using get_unit_members RPC
    const { data: unitMembers, error: membersError } = await this.supabase.rpc(
      "get_unit_members",
      { p_unit_id: businessEntity.unit_id },
    )

    if (membersError) {
      this.log.error("Error getting unit members", {
        error: membersError,
        businessEntitySlug,
      })
      throw membersError
    }

    const memberCount = unitMembers?.length ?? 0

    // 3. Get client count for this business entity
    const { count: clientCount, error: clientError } = await this.supabase
      .from("clients")
      .select("*", { count: "exact", head: true })
      .eq("business_entity_id", businessEntity.id)
      .neq("status", "archived")

    if (clientError) {
      this.log.error("Error getting client count", {
        error: clientError,
        businessEntitySlug,
      })
      throw clientError
    }

    // 4. Get project count for this business entity
    const { count: projectCount, error: projectError } = await this.supabase
      .from("projects")
      .select("*", { count: "exact", head: true })
      .eq("business_entity_id", businessEntity.id)
      .neq("status", "archived")

    if (projectError) {
      this.log.error("Error getting project count", {
        error: projectError,
        businessEntitySlug,
      })
      throw projectError
    }

    // 5. Get unique resource count (resources assigned to business entity's projects)
    const { data: resourceData, error: resourceError } = await this.supabase
      .from("project_resources")
      .select(
        `
        resource_id,
        projects!inner (
          business_entity_id
        )
      `,
      )
      .eq("projects.business_entity_id", businessEntity.id)
      .eq("status", "active")

    if (resourceError) {
      this.log.error("Error getting resource count", {
        error: resourceError,
        businessEntitySlug,
      })
      throw resourceError
    }

    // Count unique resources
    const uniqueResourceIds = new Set(
      resourceData?.map((pr) => pr.resource_id) ?? [],
    )
    const resourceCount = uniqueResourceIds.size

    return {
      memberCount,
      clientCount: clientCount ?? 0,
      projectCount: projectCount ?? 0,
      resourceCount,
    }
  }

  // Currency management methods
  async addCurrency({
    businessEntityId,
    currencyCode,
  }: AddBusinessEntityCurrencyData): Promise<{ id: string }> {
    this.log.info("Adding currency to business entity", {
      businessEntityId,
      currencyCode,
    })

    // Get the business entity's unit_id for RLS
    const { data: businessEntity, error: beError } = await this.supabase
      .from("business_entities")
      .select("unit_id")
      .eq("id", businessEntityId)
      .single()

    if (beError || !businessEntity) {
      this.log.error("Error fetching business entity for currency add", {
        error: beError,
      })
      throw new Error("Business entity not found")
    }

    const { data, error } = await this.supabase
      .from("business_entity_currencies")
      .insert({
        business_entity_id: businessEntityId,
        business_entity_unit_id: businessEntity.unit_id,
        currency_code: currencyCode,
      })
      .select("id")
      .single()

    if (error) {
      this.log.error("Error adding currency to business entity", { error })
      throw new Error(error.message)
    }

    this.log.info("Currency added to business entity successfully", {
      businessEntityId,
      currencyCode,
    })
    return { id: data.id }
  }

  async removeCurrency({
    businessEntityId,
    currencyCode,
  }: RemoveBusinessEntityCurrencyData): Promise<void> {
    this.log.info("Removing currency from business entity", {
      businessEntityId,
      currencyCode,
    })

    const { error } = await this.supabase
      .from("business_entity_currencies")
      .delete()
      .eq("business_entity_id", businessEntityId)
      .eq("currency_code", currencyCode)

    if (error) {
      this.log.error("Error removing currency from business entity", { error })
      throw new Error(error.message)
    }

    this.log.info("Currency removed from business entity successfully", {
      businessEntityId,
      currencyCode,
    })
  }

  // Invoice period management methods
  async createInvoicePeriod(
    data: CreateInvoicePeriodData,
  ): Promise<{ id: string }> {
    this.log.info("Creating invoice period", {
      businessEntityId: data.businessEntityId,
      name: data.name,
    })

    // Get the business entity's unit_id for RLS
    const { data: businessEntity, error: beError } = await this.supabase
      .from("business_entities")
      .select("unit_id")
      .eq("id", data.businessEntityId)
      .single()

    if (beError || !businessEntity) {
      this.log.error("Error fetching business entity for invoice period", {
        error: beError,
      })
      throw new Error("Business entity not found")
    }

    const { data: invoicePeriod, error } = await this.supabase
      .from("business_entity_invoice_periods")
      .insert({
        business_entity_id: data.businessEntityId,
        business_entity_unit_id: businessEntity.unit_id,
        name: data.name,
        start_date: data.startDate.toISOString(),
        end_date: data.endDate.toISOString(),
        invoice_due_date: data.invoiceDueDate.toISOString(),
        payment_date: data.paymentDate.toISOString(),
      })
      .select("id")
      .single()

    if (error) {
      this.log.error("Error creating invoice period", { error })
      throw new Error(error.message)
    }

    this.log.info("Invoice period created successfully", {
      invoicePeriodId: invoicePeriod.id,
    })
    return { id: invoicePeriod.id }
  }

  async updateInvoicePeriod(
    data: UpdateInvoicePeriodData,
  ): Promise<{ id: string }> {
    this.log.info("Updating invoice period", {
      id: data.id,
      name: data.name,
    })

    const { data: invoicePeriod, error } = await this.supabase
      .from("business_entity_invoice_periods")
      .update({
        name: data.name,
        start_date: data.startDate.toISOString(),
        end_date: data.endDate.toISOString(),
        invoice_due_date: data.invoiceDueDate.toISOString(),
        payment_date: data.paymentDate.toISOString(),
      })
      .eq("id", data.id)
      .eq("business_entity_id", data.businessEntityId)
      .select("id")
      .single()

    if (error) {
      this.log.error("Error updating invoice period", { error })
      throw new Error(error.message)
    }

    this.log.info("Invoice period updated successfully", {
      invoicePeriodId: invoicePeriod.id,
    })
    return { id: invoicePeriod.id }
  }

  async deleteInvoicePeriod({
    id,
    businessEntityId,
  }: DeleteInvoicePeriodData): Promise<void> {
    this.log.info("Deleting invoice period", { id, businessEntityId })

    const { error } = await this.supabase
      .from("business_entity_invoice_periods")
      .delete()
      .eq("id", id)
      .eq("business_entity_id", businessEntityId)

    if (error) {
      this.log.error("Error deleting invoice period", { error })
      throw new Error(error.message)
    }

    this.log.info("Invoice period deleted successfully", { id })
  }
}
